import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faRightFromBracket,
  faChevronDown,
  faKey,
  faBars
} from '@fortawesome/free-solid-svg-icons';
import api from '../api/axios';
import ConfirmModal from './ConfirmModal';
import { formatIndianDateTime } from '../utils/format';

const Header = ({ user, logout, toggleTheme, theme, settings, sidebarOpen, onToggleSidebar, onCloseSidebar }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [now, setNow] = useState(() => new Date());
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifBusy, setNotifBusy] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);

  const notifRef = useRef(null);
  const profileRef = useRef(null);

  const companyName = useMemo(() => settings?.company_name || 'Pipeline Management', [settings?.company_name]);
  const logoUrl = useMemo(() => settings?.logo_url || null, [settings?.logo_url]);

  const handleLogout = () => setLogoutConfirm(true);

  const confirmLogout = () => {
    setLogoutConfirm(false);
    setProfileOpen(false);
    onCloseSidebar?.();
    logout();
    navigate('/login');
  };

  const loadNotifications = async () => {
    try {
      setNotifBusy(true);
      const res = await api.get('/api/notifications', { params: { page: 1, limit: 12 } });
      const payload = res.data;
      const items = payload && typeof payload === 'object' && Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : []);
      const unread = payload && typeof payload === 'object' ? (payload.unread || 0) : 0;
      setNotifItems(items);
      setNotifUnread(unread);
    } catch (e) {
      setNotifItems([]);
      setNotifUnread(0);
    } finally {
      setNotifBusy(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const clockParts = useMemo(() => {
    const d = now;
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const seconds = d.getSeconds();

    const hour12 = ((hours + 11) % 12) + 1;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const timeLabel = `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
    const dateLabel = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).format(d);
    const fullLabel = `${timeLabel} • ${dateLabel}`;

    const hourAngle = (hours % 12) * 30 + minutes * 0.5;
    const minuteAngle = minutes * 6 + seconds * 0.1;
    const secondAngle = seconds * 6;

    return {
      timeLabel,
      dateLabel,
      fullLabel,
      hourAngle,
      minuteAngle,
      secondAngle
    };
  }, [now]);

  useEffect(() => {
    if (!notifOpen) return;
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifOpen]);

  useEffect(() => {
    if (!notifOpen) return;
    const onDown = (e) => {
      const node = notifRef.current;
      if (!node) return;
      if (!node.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [notifOpen]);

  useEffect(() => {
    if (!profileOpen) return;
    const onDown = (e) => {
      const node = profileRef.current;
      if (!node) return;
      if (!node.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [profileOpen]);

  const markAllRead = async () => {
    try {
      setNotifBusy(true);
      await api.post('/api/notifications/read-all');
      await loadNotifications();
    } finally {
      setNotifBusy(false);
    }
  };

  const hideNotification = async (type, id) => {
    try {
      setNotifBusy(true);
      await api.delete(`/api/notifications/${String(type || '').toLowerCase()}/${id}`);
      await loadNotifications();
    } finally {
      setNotifBusy(false);
    }
  };

  return (
    <header className="appbar">
      <div className="container">
        <div className="appbar-inner">
          <div className="appbar-left">
            {isMobile ? (
              <button
                type="button"
                className="appbar-icon-btn appbar-sidebar-toggle"
                aria-label="Toggle sidebar"
                aria-expanded={sidebarOpen}
                onClick={onToggleSidebar}
              >
                <FontAwesomeIcon icon={faBars} />
              </button>
            ) : null}
            <button
              type="button"
              className="appbar-brand"
              onClick={() => {
                setProfileOpen(false);
                navigate('/dashboard');
              }}
              title="Go to dashboard"
            >
              {logoUrl ? <img src={logoUrl} alt="Logo" className="appbar-logo" /> : null}
              <div className="appbar-brand-text">
                <div className="appbar-brand-title">{companyName}</div>
                <div className="appbar-brand-sub" />
              </div>
            </button>
          </div>

          {!isMobile ? (
            <div className="appbar-center" aria-label="Local time">
              <div className="appbar-clock" title="Local time">
                <div className="appbar-clock-face" aria-hidden="true">
                  <div className="appbar-clock-hand hour" style={{ transform: `translate(-50%, -100%) rotate(${clockParts.hourAngle}deg)` }} />
                  <div className="appbar-clock-hand minute" style={{ transform: `translate(-50%, -100%) rotate(${clockParts.minuteAngle}deg)` }} />
                  <div className="appbar-clock-hand second" style={{ transform: `translate(-50%, -100%) rotate(${clockParts.secondAngle}deg)` }} />
                  <div className="appbar-clock-dot" />
                </div>
                <div className="appbar-clock-text">
                  <div className="appbar-clock-row">
                    <span className="appbar-clock-time">{clockParts.timeLabel}</span>
                    <span className="appbar-clock-sep">•</span>
                    <span className="appbar-clock-date">{clockParts.dateLabel}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="appbar-center" />
          )}

          <div className="appbar-right">
            <button type="button" onClick={toggleTheme} className="appbar-icon-btn" title="Toggle theme">
              {theme === 'light' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M21 13a8 8 0 1 1-10-10 6 6 0 0 0 10 10Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              )}
            </button>

            <div className="notif" ref={notifRef}>
              <button
                type="button"
                className="appbar-icon-btn"
                title="Notifications"
                aria-label="Notifications"
                aria-expanded={notifOpen}
                onClick={() => setNotifOpen((v) => !v)}
              >
                <FontAwesomeIcon icon={faBell} />
                {notifUnread > 0 ? <span className="notif-badge">{notifUnread > 99 ? '99+' : notifUnread}</span> : null}
              </button>
              {notifOpen && (
                <div className="notif-menu" role="menu">
                  <div className="notif-menu-header">
                    <div className="notif-menu-title">Notifications</div>
                    <button type="button" className="glass-button" onClick={markAllRead} disabled={notifBusy}>
                      Read all
                    </button>
                  </div>
                  <div className="notif-menu-list">
                    {notifItems.length === 0 ? (
                      <div className="notif-empty">No notifications</div>
                    ) : (
                      notifItems.map((n) => (
                        <div key={`${n.type}-${n.id}`} className="notif-item">
                          <button
                            type="button"
                            className="notif-item-main"
                            onClick={() => {
                              setNotifOpen(false);
                              onCloseSidebar?.();
                              (async () => {
                                try {
                                  if (n.ts) {
                                    await api.post('/api/notifications/read-at', { ts: n.ts });
                                  }
                                } catch (e) {
                                } finally {
                                  if (n.href) navigate(n.href);
                                }
                              })();
                            }}
                          >
                            <div className="notif-item-title">{n.title}</div>
                            <div className="notif-item-sub">
                              {n.subtitle || ''}
                              {n.ts ? <span className="notif-item-time">{formatIndianDateTime(n.ts)}</span> : null}
                            </div>
                          </button>
                          <button
                            type="button"
                            className="notif-item-del"
                            onClick={() => hideNotification(n.type, n.id)}
                            disabled={notifBusy}
                            title="Delete"
                          >
                            ×
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="profile" ref={profileRef}>
              <button
                type="button"
                className="profile-trigger"
                aria-label="Profile menu"
                aria-expanded={profileOpen}
                onClick={() => setProfileOpen((v) => !v)}
              >
                <div className="profile-meta">
                  <div className="profile-name">{user.name}</div>
                  <div className="profile-role" />
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="profile-chevron" />
              </button>

              {profileOpen && (
                <div className="profile-menu" role="menu">
                  <div className="profile-menu-head">
                    <div className="profile-menu-title">{user.name}</div>
                    <div className="profile-menu-sub">{user.email}</div>
                  </div>
                  <div className="profile-menu-actions">
                    <button
                      type="button"
                      className="profile-action"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/users');
                      }}
                      disabled={user.role !== 'ADMIN'}
                      title={user.role !== 'ADMIN' ? 'Only admins can change passwords here' : undefined}
                    >
                      <FontAwesomeIcon icon={faKey} />
                      Change password
                    </button>
                    <button type="button" className="profile-action danger" onClick={handleLogout}>
                      <FontAwesomeIcon icon={faRightFromBracket} />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <ConfirmModal
          open={logoutConfirm}
          title="Logout"
          message="Are you sure you want to logout?"
          confirmText="Logout"
          cancelText="Cancel"
          onConfirm={confirmLogout}
          onCancel={() => setLogoutConfirm(false)}
        />
      </div>
    </header>
  );
};

export default Header;
